import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Flatpickr date/time picker controller
// Replaces bootstrap-datetimepicker + moment.js with modern zero-dependency picker
//
// Usage:
//   div data-controller="datepicker"
//        data-datepicker-mode-value="date"
//        data-datepicker-format-value="d-m-Y"
//        data-datepicker-locale-value="he"
//
// Options:
//   mode: "date" (default), "time", "datetime", "month"
//   format: Date format string (Flatpickr format)
//   locale: "he" (Hebrew), "en" (English)
//   enable-time: true/false
//   time-24hr: true/false (default: true)
//   min-date: Minimum selectable date
//   max-date: Maximum selectable date
//
// The "month" mode uses the flatpickr monthSelect plugin (loaded via CDN
// alongside flatpickr itself in application.slim). The value written
// back to the input follows the `format` value — for the admin AI-chat
// dashboard we use "Y-m" so the controller parses params[:month] as
// "YYYY-MM" without any string juggling.
//
export default class extends Controller {
  static targets = ["input"]  // Optional - will auto-discover if not present
  static values = {
    mode: { type: String, default: "date" },
    format: { type: String, default: "d-m-Y" },
    locale: { type: String, default: "he" },
    enableTime: { type: Boolean, default: false },
    time24hr: { type: Boolean, default: true },
    minDate: String,
    maxDate: String
  }

  connect() {
    // Flatpickr is loaded globally via CDN in application.slim
    if (typeof flatpickr === 'undefined') {
      logger.error('Flatpickr library not loaded')
      return
    }

    this.initializePicker()
  }

  // Get the input element - either explicit target or find input inside controller element
  getInputElement() {
    if (this.hasInputTarget) {
      return this.inputTarget
    }

    // For simple_form wrappers, find the input inside
    const input = this.element.querySelector('input[type="text"], input[type="string"], input:not([type])')
    if (!input) {
      logger.error('Datepicker controller: No input element found inside controller element')
    }
    return input
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy()
    }
  }

  initializePicker() {
    logger.log(`[Datepicker] ============ initializePicker() called ============`)
    logger.log(`[Datepicker] Controller element:`, this.element)

    const config = {
      dateFormat: this.formatValue,
      locale: this.localeValue,
      enableTime: this.enableTimeValue,
      time_24hr: this.time24hrValue,
      allowInput: true,
      // RTL support for Hebrew
      ...(this.localeValue === 'he' && {
        position: 'auto right',
        static: false
      })
    }

    // Add min/max dates if provided
    if (this.hasMinDateValue) {
      config.minDate = this.minDateValue
    }
    if (this.hasMaxDateValue) {
      config.maxDate = this.maxDateValue
    }

    // Mode-specific configurations
    switch (this.modeValue) {
      case 'time':
        config.enableTime = true
        config.noCalendar = true
        config.dateFormat = this.hasFormatValue ? this.formatValue : "H:i"
        break
      case 'datetime':
        config.enableTime = true
        config.dateFormat = this.hasFormatValue ? this.formatValue : "d-m-Y H:i"
        break
      case 'month':
        // Requires the monthSelect plugin loaded globally. The plugin
        // hides the day grid and shows a clickable 3×4 grid of months
        // for the active year (chevrons step year). `altInput` lets us
        // show a friendly "May 2026" label while the underlying input
        // stays in our parse-friendly "Y-m" format.
        //
        // DOM-event safety net: the plugin sets values programmatically
        // and the chain `pre-config.onChange → plugin init → setDate`
        // sometimes drops a callback set at config time, so consumers
        // using `onchange="..."` or `data-action="change->..."` get
        // nothing. Hooking the live `picker.config.onChange` array
        // AFTER init is the only place guaranteed to run on every pick.
        if (typeof monthSelectPlugin !== 'undefined') {
          config.plugins = [monthSelectPlugin({
            shorthand: false,
            dateFormat: this.hasFormatValue ? this.formatValue : "Y-m",
            altFormat:  "F Y",
            theme:      "light"
          })]
          config.dateFormat = this.hasFormatValue ? this.formatValue : "Y-m"
          config.altInput   = true
        } else {
          logger.warn('[Datepicker] monthSelect plugin not loaded; falling back to date mode')
          config.dateFormat = this.hasFormatValue ? this.formatValue : "d-m-Y"
        }
        break
      case 'date':
      default:
        config.enableTime = false
        config.dateFormat = this.hasFormatValue ? this.formatValue : "d-m-Y"
    }

    // Initialize Flatpickr on the input element
    const inputElement = this.getInputElement()
    logger.log(`[Datepicker] Input element found:`, inputElement)
    logger.log(`[Datepicker] Input element HTML:`, inputElement?.outerHTML)

    if (inputElement) {
      logger.log(`[Datepicker] Reading input.value property: "${inputElement.value}"`)
      logger.log(`[Datepicker] Input.getAttribute('value'): "${inputElement.getAttribute('value')}"`)

      // If the input already has a value, use it as the default date
      if (inputElement.value && inputElement.value.trim() !== '') {
        config.defaultDate = inputElement.value
        logger.log(`[Datepicker] Input has value: "${inputElement.value}"`)
        logger.log(`[Datepicker] Initializing with defaultDate: "${inputElement.value}"`)
        logger.log(`[Datepicker] Date format: "${config.dateFormat}"`)
      } else {
        logger.log(`[Datepicker] Input value is empty, no defaultDate set`)
        logger.log(`[Datepicker] Input placeholder: "${inputElement.placeholder}"`)
      }

      // Parse compact typed input (ddmmyy / ddmmyyyy) before flatpickr's
      // own parser runs, so "010126" becomes Jan 1 2026 instead of Dec 1 2026.
      config.parseDate = (dateStr, format) => {
        const raw = dateStr.replace(/\s/g, "")
        if (/^\d{6}$/.test(raw) || /^\d{8}$/.test(raw)) {
          const dd = parseInt(raw.slice(0, 2), 10)
          const mm = parseInt(raw.slice(2, 4), 10) - 1
          let yyyy = raw.slice(4)
          if (yyyy.length === 2) {
            yyyy = (parseInt(yyyy, 10) > 68 ? "19" : "20") + yyyy
          }
          return new Date(parseInt(yyyy, 10), mm, dd)
        }
        // Fall back to flatpickr's default parser
        return flatpickr.parseDate(dateStr, format)
      }

      this.picker = flatpickr(inputElement, config)

      // Log the picker's selected date after initialization
      if (this.picker.selectedDates && this.picker.selectedDates.length > 0) {
        logger.log(`[Datepicker] Picker initialized with selected date: ${this.picker.selectedDates[0]}`)
      } else {
        logger.warn(`[Datepicker] Picker initialized but no date selected. Input value was: "${inputElement.value}"`)
        // Try to manually set the date if the input has a value but Flatpickr didn't pick it up
        if (inputElement.value && inputElement.value.trim() !== '') {
          logger.log(`[Datepicker] Attempting to manually set date: "${inputElement.value}"`)
          this.picker.setDate(inputElement.value, false, config.dateFormat)
        }
      }
    }
  }

  // Allow programmatic date setting
  setDate(date) {
    if (this.picker) {
      this.picker.setDate(date)
    }
  }

  // Clear the date
  clear() {
    if (this.picker) {
      this.picker.clear()
    }
  }
}
